import React, { useState, useEffect } from 'react';
import { X, Bell, ArrowRight, MessageCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { dailyTaskService } from '@/services/dailyTaskService';

interface PendingTask {
  id: string;
  type: 'social_media' | 'journal_entry';
  title: string;
  description: string;
  route: string;
}

interface CashpurrNotificationProps {
  onClose: () => void;
  task: PendingTask;
}

const CashpurrNotificationPopup: React.FC<CashpurrNotificationProps> = ({ onClose, task }) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateCashpurrMessage();
  }, [task]);

  const generateCashpurrMessage = async () => {
    setMessage(getDefaultMessage());
    setLoading(false);
  };

  const getDefaultMessage = () => {
    if (task.type === 'social_media') {
      return "Meow! 📱 Time to post and boost your startup presence! Let's make it happen! 🚀";
    } else {
      return "Meow! 📝 Your daily journal is calling! Capture today's wins and insights! ✨";
    }
  };

  const handleCompleteNow = () => {
    navigate(task.route);
    handleClose();
  };

  const handleChatWithCashpurr = () => {
    // Open Cashpurr chat with pre-filled message about the task
    const taskContext = `I need help with my ${task.type === 'social_media' ? 'social media posting' : 'journal entry'} task: ${task.title}. ${task.description}`;
    
    window.dispatchEvent(new CustomEvent('open-cashpurr', {
      detail: {
        prefill: taskContext,
        meta: { source: 'daily_task_notification', taskId: task.id }
      }
    }));
    
    handleClose();
  };

  const handleClose = async () => {
    try {
      // Save the close event to prevent repeated popups
      await supabase.from('notifications').insert({
        user_id: (await supabase.auth.getUser()).data.user?.id,
        content: `Cashpurr notification closed for task: ${task.title}`,
        type: 'popup_closed',
        context: {
          task_id: task.id,
          task_type: task.type,
          closed_at: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Error saving close event:', error);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-teal-50 via-white to-orange-50 rounded-2xl shadow-2xl max-w-md w-full border-2 border-teal-200 animate-scale-in">
        {/* Header with close button */}
        <div className="flex justify-between items-start p-6 pb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-orange-400 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-2xl">🐱</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg">Cashpurr Says</h3>
              <p className="text-sm text-gray-600">Your AI Success Coach</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message content */}
        <div className="px-6 pb-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 mb-4 border border-teal-100">
            <div className="flex items-start space-x-3">
              <Bell className="w-5 h-5 text-teal-600 mt-0.5 animate-pulse" />
              <div className="flex-1">
                {loading ? (
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                  </div>
                ) : (
                  <p className="text-gray-700 font-medium leading-relaxed">{message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Task info */}
          <div className="bg-teal-50 rounded-lg p-3 mb-4 border border-teal-200">
            <h4 className="font-semibold text-teal-800 text-sm">{task.title}</h4>
            <p className="text-teal-600 text-xs mt-1">{task.description}</p>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-2">
            <button
              onClick={handleCompleteNow}
              className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              <span>Complete Now</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleChatWithCashpurr}
              className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Chat</span>
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors font-medium"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashpurrNotificationPopup;